import { Text } from "@clipboard-health/ui-components";
// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { LiveActivity } from "@clipboardhealth/capacitor-live-activity";
import { Box, Button, Table, TableBody, TableCell, TableRow, useTheme } from "@mui/material";
import { type ReactElement, useState } from "react";

import {
  buildLiveActivityScenarios,
  type LiveActivityScenario,
  SCENARIO_KEYS,
  TestResult,
  type TestResultType,
} from "./liveActivityDebuggerScenarios";

// Listen for Live Activity push token events and log them for manual testing
if (typeof LiveActivity?.addListener === "function") {
  void LiveActivity.addListener(
    "liveActivityPushToken",
    ({ activityId, pushToken }: { activityId: string; pushToken: string }) => {
      // eslint-disable-next-line no-console
      console.log("[LiveActivity] Received push token for activity:", { activityId, pushToken });
    }
  );
}

export function LiveActivityDebugger(): ReactElement {
  const [isActivityActive, setIsActivityActive] = useState<boolean>(false);

  function createScenarios(): LiveActivityScenario[] {
    return buildLiveActivityScenarios({
      onActivityStart: () => {
        setIsActivityActive(true);
      },
      onActivityEnd: () => {
        setIsActivityActive(false);
      },
    });
  }

  const [scenarios, setScenarios] = useState<LiveActivityScenario[]>(createScenarios);
  const theme = useTheme();

  function isButtonEnabled(scenarioKey: LiveActivityScenario["key"]): boolean {
    if (scenarioKey === SCENARIO_KEYS.END_LIVE_ACTIVITY) {
      return isActivityActive;
    }

    // All scenarios are always available for testing
    return true;
  }

  function getStatusColor(status: TestResultType): string {
    switch (status) {
      case TestResult.PENDING: {
        return theme.palette.text.primary;
      }

      case TestResult.SUCCESS: {
        return theme.palette.success.main;
      }

      case TestResult.FAILURE: {
        return theme.palette.error.main;
      }

      default: {
        return theme.palette.text.primary;
      }
    }
  }

  return (
    <Box>
      <Text variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
        Live Activities Debugging (V2 Schema)
      </Text>
      <Text variant="body2" sx={{ mb: 2, fontStyle: "italic" }}>
        Activity Status: {isActivityActive ? "Active" : "Inactive"}
      </Text>
      <Text variant="caption" sx={{ mb: 2, display: "block", color: "text.secondary" }}>
        Click any scenario to test that state. Will create a new Live Activity or update the
        existing one:
      </Text>
      <Text variant="caption" sx={{ mb: 2, display: "block", color: "text.secondary" }}>
        • Pre-Departure View: 6 different timing states (no timing, early, leave soon, leave now,
        late, stale ETA)
      </Text>
      <Text variant="caption" sx={{ mb: 2, display: "block", color: "text.secondary" }}>
        • Live ETA View: Trip in progress with progress bar
      </Text>
      <Text variant="caption" sx={{ mb: 2, display: "block", color: "text.secondary" }}>
        • Post-Trip Arrival View: Arrived at destination, ready to clock in
      </Text>
      <Table>
        <TableBody>
          {scenarios.map((scenario) => (
            <TableRow key={`live-activity-scenario-${scenario.key}`}>
              <TableCell>
                <Button
                  size="small"
                  sx={{ fontSize: "10px", width: "200px" }}
                  variant="contained"
                  disabled={!isButtonEnabled(scenario.key)}
                  onClick={async () => {
                    const result = await scenario.execute();
                    setScenarios((previousScenarios) => {
                      return previousScenarios.map((s) =>
                        s.key === scenario.key ? { ...s, status: result } : s
                      );
                    });
                  }}
                >
                  {scenario.displayName}
                </Button>
              </TableCell>
              <TableCell>
                <Text color={getStatusColor(scenario.status)}>
                  {scenario.displayName}: {scenario.status}
                </Text>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Button
        fullWidth
        sx={{ fontSize: "10px", mt: 1 }}
        variant="outlined"
        onClick={() => {
          setIsActivityActive(false);
          setScenarios(createScenarios());
        }}
      >
        Reset Live Activity Tests
      </Button>
    </Box>
  );
}
